/**
 * @krisspy-file
 * @type page
 * @name "ProductForm"
 * @title "Product Form"
 * @description "Create or edit product details"
 * @routes ["/products/create"]
 * @flowName "product-management"
 * @layout "DashboardLayout"
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import NumberInput from '../components/NumberInput';
import {
  ArrowLeft,
  Save,
  X,
  Upload,
  Trash2,
  Package,
  DollarSign,
  MapPin,
  AlertCircle,
  Loader2,
  Info,
  Settings,
  Tag,
  Link as LinkIcon,
  Languages,
  BarChart3,
  Clock,
  RotateCcw,
  Copy,
  Wrench
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';
import { getProductById, getProducts, createProduct, updateProduct, deleteProduct, upsertProductTranslation, getProductTranslations, deleteProductTranslation, copyProduct } from '../services/products.service';
import { getLocations } from '../services/locations.service';
import { getCategories } from '../services/categories.service';
import { getMerchantById } from '../services/merchants.service';
import { useAuth } from '../contexts/AuthContext';
import { useBarcodeScanning } from '../contexts/ScanContext';
import { parseBarcodeDuplicate } from '../lib/barcode';
import { useLanguage } from '../contexts/LanguageContext';
import { JsonbTableEditor } from '../components/JsonbTableEditor';
import { PricingRulesEditor, type PricingRulesEditorRef } from '../components/PricingRulesEditor';
import { ProductAddonsManager } from '../components/ProductAddonsManager';
import { saveAddonChanges } from '../services/productAddons.service';
import { savePricingRuleChanges } from '../services/pricingRules.service';
import { uploadProductImage } from '../lib/storage';
import CopyProductDialog from '../components/CopyProductDialog';

// Must match the product_types_variant_type_check constraint in the database.
const VARIANT_TYPES = ['none', 'size', 'color', 'number', 'model'] as const;
const VARIANT_TYPE_LABEL_KEYS: Record<(typeof VARIANT_TYPES)[number], string> = {
  none: 'products.variantTypeNone',
  size: 'products.variantTypeSize',
  color: 'products.variantTypeColor',
  number: 'products.variantTypeNumber',
  model: 'products.variantTypeModel',
};
const isVariantType = (value: string): value is (typeof VARIANT_TYPES)[number] =>
  (VARIANT_TYPES as readonly string[]).includes(value);

type TabType = 'general' | 'details' | 'inventory' | 'display' | 'pricing' | 'addons' | 'usage';

export default function ProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { currentLanguage, availableLanguages, defaultLanguage } = useLanguage();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();
  const { enabled: scanningEnabled } = useBarcodeScanning();
  const isEditMode = !!id;

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [activeLanguageTab, setActiveLanguageTab] = useState(defaultLanguage);

  // Form state
  const [formData, setFormData] = useState({
    product_name: '',
    description: '',
    product_type: '',
    model: '',
    category_id: '',
    location_id: '',
    active: true,
    featured: false,
    hidden: false,
    picture_url: '',
    display_order: 0,
    sizes: '',
    features: '',
    maintenance_time_hours: 0,
    pre_booking_hours: 0,
    inventory_type: 'individual',
    pool_quantity: null as number | null,
    barcode: '',
    max_concurrent_rentals: null as number | null,
    hide_stock_count: false,
    cutoff_days_prior: null as number | null,
    cutoff_time: '',
    bundle_components: '',
    available_location_ids: [] as string[],
    delivery_eligible: false,
    variant_type: '',
    variant_label: '',
    variant_description_label: '',
    product_specific_info: '',
    // Maintenance check intervals
    maintenance_check_interval_hours: null as number | null,
    maintenance_check_interval_uses: null as number | null,
    // Usage statistics (read-only, populated from DB)
    usage_times: 0,
    usage_hours: 0,
    usage_revenue: 0
  });

  const [images, setImages] = useState<{ id: string; url: string; isPrimary: boolean }[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categories, setCategories] = useState<Array<{ id: string; category_name: string }>>([]);
  const [locations, setLocations] = useState<Array<{ id: string; location_name: string }>>([]);
  const [products, setProducts] = useState<Array<{ id: string; product_name: string }>>([]);
  const [currency, setCurrency] = useState<string>('EUR');
  const [addonChanges, setAddonChanges] = useState<any[]>([]);
  const [pricingRuleChanges, setPricingRuleChanges] = useState<any[]>([]);
  const pricingRulesRef = useRef<PricingRulesEditorRef>(null);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copying, setCopying] = useState(false);

  // Translations for each language
  const [translations, setTranslations] = useState<Record<string, {
    product_name: string;
    description: string;
    variant_label: string;
    variant_description_label: string;
    product_specific_info: string;
    features: string;
  }>>({});

  useEffect(() => {
    loadFormData();
  }, [id, defaultLanguage]);

  async function loadFormData() {
    setLoading(true);

    const [categoriesResult, locationsResult, productsResult] = await Promise.all([
      getCategories({ merchantId, locationIds, isPlatformAdmin }, {}, defaultLanguage, defaultLanguage),
      getLocations({ merchantId, locationIds, isPlatformAdmin }, {}, defaultLanguage, defaultLanguage),
      getProducts({ merchantId, locationIds, isPlatformAdmin }, {}, defaultLanguage, defaultLanguage)
    ]);

    if (categoriesResult.data) {
      setCategories(categoriesResult.data);
    }

    if (locationsResult.data) {
      setLocations(locationsResult.data);
    }

    if (productsResult.data) {
      setProducts(productsResult.data);
    }

    if (merchantId) {
      const { data: merchant } = await getMerchantById(merchantId, defaultLanguage, defaultLanguage);
      if (merchant?.currency_code) {
        setCurrency(merchant.currency_code);
      }
    }

    if (isEditMode && id) {
      // Load raw base data — pass defaultLanguage to skip translation merge for the default language
      const { data: product, error } = await getProductById(id, { merchantId, locationIds, isPlatformAdmin }, defaultLanguage, defaultLanguage);

      if (error) {
        showToast(t('products.failedToLoad') + ': ' + error, 'error');
        navigate('/products');
        return;
      }

      if (product) {
        console.log('Loaded product:', product); // Debug log
        setFormData({
          product_name: product.product_name || '',
          description: product.description || '',
          product_type: product.product_type || '',
          model: product.model || '',
          category_id: product.category_id || '',
          location_id: product.location_id || '',
          active: product.active ?? true,
          featured: product.featured ?? false,
          hidden: product.hidden ?? false,
          picture_url: product.picture_url || '',
          display_order: product.display_order || 0,
          sizes: product.sizes ? JSON.stringify(product.sizes, null, 2) : '',
          features: product.features ? JSON.stringify(product.features, null, 2) : '',
          maintenance_time_hours: product.maintenance_time_hours || 0,
          pre_booking_hours: product.pre_booking_hours || 0,
          inventory_type: product.inventory_type || 'individual',
          pool_quantity: product.pool_quantity,
          barcode: (product as any).barcode || '',
          max_concurrent_rentals: product.max_concurrent_rentals,
          hide_stock_count: product.hide_stock_count ?? false,
          cutoff_days_prior: product.cutoff_days_prior ?? null,
          cutoff_time: product.cutoff_time || '',
          bundle_components: product.bundle_components ? JSON.stringify(product.bundle_components, null, 2) : '',
          available_location_ids: product.available_location_ids || [],
          delivery_eligible: product.delivery_eligible ?? false,
          variant_type: product.variant_type?.trim().toLowerCase() || '',
          variant_label: product.variant_label || '',
          variant_description_label: product.variant_description_label || '',
          product_specific_info: product.product_specific_info || '',
          // Maintenance check intervals
          maintenance_check_interval_hours: product.maintenance_check_interval_hours ?? null,
          maintenance_check_interval_uses: product.maintenance_check_interval_uses ?? null,
          // Usage statistics
          usage_times: product.usage_times || 0,
          usage_hours: product.usage_hours || 0,
          usage_revenue: product.usage_revenue || 0
        });

        if (product.picture_url) {
          setImages([{
            id: 'existing-1',
            url: product.picture_url,
            isPrimary: true
          }]);
        }

        // Load all translations (excluding default language - that's in the main table)
        const { data: translationsData } = await getProductTranslations(id);
        if (translationsData) {
          const translationsMap: Record<string, { product_name: string; description: string; variant_label: string; variant_description_label: string; product_specific_info: string; features: string }> = {};
          translationsData.forEach((t) => {
            // Only load translations for non-default languages
            if (t.language_code !== defaultLanguage) {
              translationsMap[t.language_code] = {
                product_name: t.product_name || '',
                description: t.description || '',
                variant_label: t.variant_label || '',
                variant_description_label: t.variant_description_label || '',
                product_specific_info: t.product_specific_info || '',
                features: t.features ? JSON.stringify(t.features, null, 2) : ''
              };
            }
          });
          setTranslations(translationsMap);
        }
      }
    }

    setLoading(false);
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      // Upload using storage utility (handles validation and bucket creation)
      const publicUrl = await uploadProductImage(file, merchantId || 'default');

      // Replace existing image (only one image supported)
      const newImage = {
        id: `img-${Date.now()}`,
        url: publicUrl,
        isPrimary: true
      };

      setImages([newImage]);
      setFormData(prev => ({ ...prev, picture_url: publicUrl }));
    } catch (error: any) {
      console.error('Error uploading image:', error);
      showToast(error.message || 'Failed to upload image', 'error');
    } finally {
      setLoading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleRemoveImage = (_id: string) => {
    // Clear the image and picture_url
    setImages([]);
    setFormData(prev => ({ ...prev, picture_url: '' }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.product_name.trim()) {
      newErrors.product_name = 'Product name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.product_type.trim()) {
      newErrors.product_type = 'Product type is required';
    }
    if (!formData.location_id) {
      newErrors.location_id = 'Location is required';
    }
    if (formData.variant_type && !isVariantType(formData.variant_type)) {
      newErrors.variant_type = t('products.variantTypeInvalid', { value: formData.variant_type });
    }

    setErrors(newErrors);
    if (newErrors.variant_type) {
      showToast(newErrors.variant_type, 'error');
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Auto-apply any in-progress pricing rule edit before saving
    // Use the returned array directly since setState is async
    const flushedRules = pricingRulesRef.current?.flushEditing();

    if (!validateForm()) return;

    setLoading(true);

    // If flushEditing returned rules, use them (state may not have updated yet)
    const effectivePricingRules = flushedRules ?? pricingRuleChanges;

    try {
      // Parse JSON fields
      let sizesJson = null;
      let featuresJson = null;
      let bundleComponentsJson = null;

      if (formData.sizes.trim()) {
        try {
          sizesJson = JSON.parse(formData.sizes);
        } catch (e) {
          showToast('Invalid JSON format in Sizes field', 'error');
          setLoading(false);
          return;
        }
      }

      if (formData.features.trim()) {
        try {
          featuresJson = JSON.parse(formData.features);
        } catch (e) {
          showToast('Invalid JSON format in Features field', 'error');
          setLoading(false);
          return;
        }
      }

      if (formData.bundle_components.trim()) {
        try {
          const parsed = JSON.parse(formData.bundle_components);
          bundleComponentsJson = Array.isArray(parsed) && parsed.length === 0 ? null : parsed;
        } catch (e) {
          showToast('Invalid JSON format in Bundle Components field', 'error');
          setLoading(false);
          return;
        }
      }

      let productId = id;

      // Admin UI language only affects chrome — content is always in the merchant's default language
      const isEditingDefaultLanguage = true;

      if (isEditingDefaultLanguage) {
        // Editing default language - save to main table
        const productData = {
          product_name: formData.product_name,
          description: formData.description,
          product_type: formData.product_type,
          model: formData.model || null,
          category_id: formData.category_id || null,
          location_id: formData.location_id,
          active: formData.active,
          featured: formData.featured,
          hidden: formData.hidden,
          picture_url: formData.picture_url || null,
          display_order: formData.display_order,
          sizes: sizesJson,
          features: featuresJson,
          maintenance_time_hours: formData.maintenance_time_hours || null,
          maintenance_check_interval_hours: formData.maintenance_check_interval_hours || null,
          maintenance_check_interval_uses: formData.maintenance_check_interval_uses || null,
          pre_booking_hours: formData.pre_booking_hours || null,
          inventory_type: formData.inventory_type || null,
          pool_quantity: formData.pool_quantity,
          max_concurrent_rentals: formData.max_concurrent_rentals,
          // Add-ons never show a stock count in the booking flow, so the flag
          // is only meaningful (and only editable) for rental products and sets.
          hide_stock_count: formData.product_type === 'addon' ? false : formData.hide_stock_count,
          cutoff_days_prior: formData.cutoff_days_prior,
          cutoff_time: formData.cutoff_time || null,
          bundle_components: bundleComponentsJson,
          available_location_ids: formData.available_location_ids.length > 0 ? formData.available_location_ids : null,
          delivery_eligible: formData.delivery_eligible,
          variant_type: formData.variant_type || null,
          variant_label: formData.variant_label || null,
          variant_description_label: formData.variant_description_label || null,
          product_specific_info: formData.product_specific_info || null,
          // Barcode is only written while scanning is on, so saving a product for a
          // merchant without scanning never touches the column.
          ...(scanningEnabled ? { barcode: formData.barcode.trim() || null } : {}),
        };

        if (isEditMode && id) {
          const { error } = await updateProduct(id, productData);
          if (error) {
            const duplicateOwner = parseBarcodeDuplicate(error);
            showToast(
              duplicateOwner
                ? t('scan.barcodeDuplicate', { owner: duplicateOwner })
                : t('products.failedToUpdate') + ': ' + error,
              'error'
            );
            setLoading(false);
            return;
          }

          // Clean up any stale default-language translation row so it can't
          // override the freshly-updated main-table value on later reads.
          if (defaultLanguage) {
            await deleteProductTranslation(id, defaultLanguage);
          }
        } else {
          const { data: newProduct, error } = await createProduct(productData);
          if (error) {
            const duplicateOwner = parseBarcodeDuplicate(error);
            showToast(
              duplicateOwner
                ? t('scan.barcodeDuplicate', { owner: duplicateOwner })
                : t('products.failedToCreate') + ': ' + error,
              'error'
            );
            setLoading(false);
            return;
          }
          if (newProduct) {
            productId = newProduct.id;
          }
        }
      } else {
        // Editing translation - save to translation table
        if (!isEditMode || !id) {
          showToast('Cannot create new product in non-default language. Please switch to ' + defaultLanguage.toUpperCase(), 'warning');
          setLoading(false);
          return;
        }

        // Save translation
        await upsertProductTranslation(id, currentLanguage, {
          product_name: formData.product_name || null,
          description: formData.description || null,
          variant_label: formData.variant_label || null,
          variant_description_label: formData.variant_description_label || null,
          product_specific_info: formData.product_specific_info || null
        });
      }

      // Save addon and pricing changes (only for default language edits)
      if (isEditingDefaultLanguage && isEditMode && id) {
        // Save addon changes if this is not an addon product
        if (formData.product_type !== 'addon' && addonChanges.length > 0) {
          const { error: addonError } = await saveAddonChanges(id, addonChanges);
          if (addonError) {
            showToast(t('products.updatedButAddonFailed') + ': ' + addonError, 'warning');
            setLoading(false);
            return;
          }
        }

        // Save pricing rule changes
        if (effectivePricingRules.length > 0) {
          const { error: pricingError } = await savePricingRuleChanges(effectivePricingRules);
          if (pricingError) {
            showToast(t('products.updatedButPricingFailed') + ': ' + pricingError, 'warning');
            setLoading(false);
            return;
          }
        }
      }

      // Save translations from translation tabs (skip default language - that's the main table)
      if (productId && isEditingDefaultLanguage) {
        for (const lang of availableLanguages) {
          if (lang.code === defaultLanguage) continue;

          const translation = translations[lang.code];
          if (translation && (translation.product_name || translation.description || translation.variant_label || translation.variant_description_label || translation.product_specific_info || translation.features)) {
            let parsedFeatures = null;
            if (translation.features) {
              try { parsedFeatures = JSON.parse(translation.features); } catch { /* keep null */ }
            }
            await upsertProductTranslation(productId, lang.code, {
              product_name: translation.product_name || null,
              description: translation.description || null,
              variant_label: translation.variant_label || null,
              variant_description_label: translation.variant_description_label || null,
              product_specific_info: translation.product_specific_info || null,
              features: parsedFeatures
            });
          } else if (translation) {
            // Loaded/edited but left empty → delete so the cleared translation persists.
            await deleteProductTranslation(productId, lang.code);
          }
        }
      }

      showToast(isEditMode ? t('products.updatedSuccessfully') : t('products.createdSuccessfully'), 'success');
      navigate('/products');
    } catch (error) {
      showToast('An error occurred while saving the product', 'error');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setDeleting(true);
    setErrors({});

    const { error: deleteError } = await deleteProduct(id);

    if (deleteError) {
      setErrors({ general: deleteError });
      setDeleting(false);
      // Keep modal open to show the error and deactivate option
      return;
    }

    // Navigate with state to trigger reload
    navigate('/products', { state: { reload: Date.now() } });
  };

  const handleDeactivate = async () => {
    if (!id) return;

    setDeleting(true);
    setErrors({});

    const { error: updateError } = await updateProduct(id, {
      active: false,
      updated_at: new Date().toISOString()
    });

    if (updateError) {
      setErrors({ general: updateError });
      setDeleting(false);
      setShowDeleteConfirm(false);
      return;
    }

    // Navigate with state to trigger reload
    navigate('/products', { state: { reload: Date.now() } });
  };

  const handleCancel = () => {
    navigate('/products');
  };

  const handleCopy = async (options: {
    newName: string;
    targetLocationId: string;
    copyPricingRules: boolean;
    copyTranslations: boolean;
    copyInventoryItems: boolean;
  }) => {
    if (!id) return;

    setCopying(true);
    const { data: newProduct, error } = await copyProduct(id, options);
    setCopying(false);

    if (error) {
      showToast(t('products.failedToCopy') + ': ' + error, 'error');
      return;
    }

    setShowCopyDialog(false);
    showToast(t('products.copiedSuccessfully'), 'success');

    if (newProduct) {
      navigate(`/products/${newProduct.id}/edit`);
    }
  };

  const tabs = [
    { id: 'general' as TabType, label: t('products.generalInformation'), icon: Info },
    { id: 'details' as TabType, label: t('products.detailsVariants'), icon: Tag },
    { id: 'inventory' as TabType, label: t('products.inventoryBooking'), icon: Package },
    { id: 'display' as TabType, label: t('products.displaySettings'), icon: Settings },
    ...(isEditMode ? [{ id: 'pricing' as TabType, label: t('products.pricingRules'), icon: DollarSign }] : []),
    ...(isEditMode && formData.product_type !== 'addon' ? [{ id: 'addons' as TabType, label: t('products.linkedAddons'), icon: LinkIcon }] : []),
    ...(isEditMode ? [{ id: 'usage' as TabType, label: t('products.usageStatistics'), icon: BarChart3 }] : [])
  ];

  if (loading && isEditMode) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="card">
          <div className="flex items-center justify-center py-2xl">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <span className="ml-md text-secondary">{t('products.loadingProduct')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-md">
        <div className="flex items-center gap-md">
          <button onClick={() => navigate('/products')} className="btn-icon">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-sm">
              {isEditMode ? t('products.editProduct') : t('products.createProduct')}
            </h1>
            <p className="text-sm sm:text-base text-secondary">
              {isEditMode ? t('products.updateProductDetails') : t('products.addNewRentalProduct')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-sm sm:gap-md flex-shrink-0">
          {isEditMode && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn btn-ghost text-danger hover:bg-danger/10 text-sm"
              disabled={loading || deleting}
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">Delete</span>
            </button>
          )}
          {isEditMode && (
            <button
              onClick={() => setShowCopyDialog(true)}
              className="btn btn-ghost text-sm"
              disabled={loading || deleting}
            >
              <Copy className="w-4 h-4" />
              <span className="hidden sm:inline">{t('products.copyProduct')}</span>
            </button>
          )}
          <button onClick={handleCancel} className="btn btn-ghost text-sm" disabled={loading || deleting}>
            <X className="w-4 h-4" />
            <span className="hidden sm:inline">{t('common.cancel')}</span>
          </button>
          <button onClick={handleSave} className="btn btn-primary text-sm" disabled={loading || deleting}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">{t('common.saving')}</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">{isEditMode ? t('products.updateProduct') : t('common.save')}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="card p-0 overflow-hidden">
        <div className="flex border-b border-primary overflow-x-auto scrollbar-thin">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-sm px-sm sm:px-md lg:px-lg py-sm sm:py-md text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue border-b-2 border-blue bg-blue/5'
                    : 'text-secondary hover:text-primary hover:bg-secondary'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-sm sm:p-md lg:p-lg">
          {/* Tab 1: General Information */}
          {activeTab === 'general' && (
            <div className="space-y-lg">
              {/* Basic Info Section */}
              <div className="space-y-lg">
                <div>
                  <label htmlFor="product_name" className="block text-sm font-medium text-primary mb-sm">
                    {t('products.productName')} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="product_name"
                    name="product_name"
                    value={formData.product_name}
                    onChange={handleInputChange}
                    placeholder={t('products.productNamePlaceholder')}
                    className={`input w-full ${errors.product_name ? 'border-danger' : ''}`}
                  />
                  {errors.product_name && (
                    <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                      <AlertCircle className="w-3 h-3" />
                      {errors.product_name}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-primary mb-sm">
                    {t('products.descriptionLabel')} <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder={t('products.descriptionPlaceholder')}
                    rows={4}
                    className={`input w-full resize-none ${errors.description ? 'border-danger' : ''}`}
                  />
                  {errors.description && (
                    <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                      <AlertCircle className="w-3 h-3" />
                      {errors.description}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
                  <div>
                    <label htmlFor="product_type" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.productTypeLabel')} <span className="text-danger">*</span>
                    </label>
                    {/* Check if current value is a legacy/custom value not in standard options */}
                    {isEditMode && formData.product_type && !['bike', 'addon', 'other'].includes(formData.product_type) ? (
                      // Legacy value - show as disabled input
                      <input
                        type="text"
                        id="product_type"
                        value={formData.product_type}
                        disabled
                        className="input w-full bg-secondary opacity-60 cursor-not-allowed"
                        title="This is a legacy value and cannot be changed"
                      />
                    ) : (
                      // Standard dropdown for new products or products with standard types
                      <select
                        id="product_type"
                        name="product_type"
                        value={formData.product_type}
                        onChange={handleInputChange}
                        className={`input w-full ${errors.product_type ? 'border-danger' : ''}`}
                      >
                        <option value="">{t('products.selectProductType')}</option>
                        <option value="bike">Bike</option>
                        <option value="addon">Add-on</option>
                        <option value="other">Other</option>
                      </select>
                    )}
                    {errors.product_type && (
                      <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                        <AlertCircle className="w-3 h-3" />
                        {errors.product_type}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="model" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.modelLabel')}
                    </label>
                    <input
                      type="text"
                      id="model"
                      name="model"
                      value={formData.model}
                      onChange={handleInputChange}
                      placeholder={t('products.modelPlaceholder')}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="category_id" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.categoryLabel')}
                    </label>
                    <select
                      id="category_id"
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleInputChange}
                      className="input w-full"
                    >
                      <option value="">{t('products.selectCategoryOptional')}</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.category_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="location_id" className="block text-sm font-medium text-primary mb-sm">
                    {t('products.primaryLocationLabel')} <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <select
                      id="location_id"
                      name="location_id"
                      value={formData.location_id}
                      onChange={handleInputChange}
                      className={`input pl-10 w-full ${errors.location_id ? 'border-danger' : ''}`}
                    >
                      <option value="">{t('products.selectLocation')}</option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.location_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.location_id && (
                    <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                      <AlertCircle className="w-3 h-3" />
                      {errors.location_id}
                    </p>
                  )}
                </div>

                {/* Available Locations - Multi-select */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('products.availableLocationsLabel')}
                  </label>
                  <div className="border border-primary rounded-lg p-md max-h-48 overflow-y-auto">
                    {locations.filter(loc => loc.id !== formData.location_id).length === 0 ? (
                      <p className="text-sm text-secondary italic">{t('products.noOtherLocationsAvailable')}</p>
                    ) : (
                      <div className="space-y-sm">
                        {locations
                          .filter(loc => loc.id !== formData.location_id)
                          .map((loc) => (
                            <label key={loc.id} className="flex items-center gap-sm cursor-pointer hover:bg-secondary p-sm rounded-md -m-sm">
                              <input
                                type="checkbox"
                                checked={formData.available_location_ids.includes(loc.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData(prev => ({
                                      ...prev,
                                      available_location_ids: [...prev.available_location_ids, loc.id]
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      available_location_ids: prev.available_location_ids.filter(id => id !== loc.id)
                                    }));
                                  }
                                }}
                                className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                              />
                              <span className="text-sm text-primary">{loc.location_name}</span>
                            </label>
                          ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-secondary mt-xs">
                    {t('products.availableLocationsHelperText')}
                  </p>
                </div>
              </div>

              {/* Features Section */}
              <div className="border-t border-primary pt-lg">
                <JsonbTableEditor
                  value={formData.features}
                  onChange={(value) => setFormData(prev => ({ ...prev, features: value }))}
                  label={t('products.featuresLabel')}
                  placeholder='["750Wh battery", "Bosch CX motor", "All-terrain tires"]'
                  helperText={t('products.featuresHelperText')}
                />
              </div>

              {/* Images Section */}
              <div className="border-t border-primary pt-lg">
                <h3 className="text-lg font-semibold text-primary mb-md">{t('products.productImageLabel')}</h3>

                <label
                  htmlFor="product-image-upload"
                  className="w-full border-2 border-dashed border-primary rounded-lg p-lg hover:bg-secondary transition-colors mb-lg cursor-pointer block"
                >
                  <div className="flex flex-col items-center gap-md">
                    <div className="w-12 h-12 rounded-full bg-blue/10 flex items-center justify-center">
                      <Upload className="w-6 h-6 text-blue" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-primary mb-xs">
                        {images.length > 0 ? t('products.clickToReplaceImage') : t('products.clickToUploadImage')}
                      </p>
                      <p className="text-xs text-secondary">{t('products.imageTypeSize')}</p>
                    </div>
                  </div>
                  <input
                    id="product-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={loading}
                  />
                </label>

                {images.length > 0 && (
                  <div className="space-y-md">
                    <p className="text-sm font-medium text-primary">{t('products.currentProductImage')}</p>
                    <div className="relative inline-block">
                      <img
                        src={images[0].url}
                        alt="Product"
                        className="w-48 h-48 object-cover rounded-md border border-primary"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(images[0].id)}
                        className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                        title="Remove image"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Product Specific Info Section */}
              <div className="border-t border-primary pt-lg">
                <div>
                  <label htmlFor="product_specific_info" className="block text-sm font-medium text-primary mb-sm">
                    {t('products.productSpecificInfo')}
                  </label>
                  <textarea
                    id="product_specific_info"
                    name="product_specific_info"
                    value={formData.product_specific_info}
                    onChange={handleInputChange}
                    placeholder={t('products.productSpecificInfoPlaceholder')}
                    rows={3}
                    className="input w-full resize-none"
                  />
                  <p className="text-xs text-secondary mt-xs">
                    {t('products.productSpecificInfoHelper')}
                  </p>
                </div>
              </div>

              {/* Translations Section */}
              {availableLanguages.length > 1 && (
                <div className="border-t border-primary pt-lg">
                  <div className="flex items-center gap-md mb-lg">
                    <Languages className="w-5 h-5 text-blue" />
                    <h3 className="text-lg font-semibold text-primary">{t('products.translations')}</h3>
                  </div>

                  {/* Language Tabs */}
                  <div className="flex gap-sm mb-lg border-b border-primary/10 overflow-x-auto">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setActiveLanguageTab(lang.code)}
                        className={`px-md py-sm text-sm font-medium whitespace-nowrap transition-colors ${
                          activeLanguageTab === lang.code
                            ? 'text-blue border-b-2 border-blue'
                            : 'text-secondary hover:text-primary'
                        }`}
                      >
                        {lang.flag} {lang.label}
                      </button>
                    ))}
                  </div>

                  {/* Translation Fields */}
                  <div className="space-y-lg">
                    {activeLanguageTab === defaultLanguage ? (
                      /* Show main table data for default language */
                      <div className="p-md bg-blue/5 border border-blue/20 rounded-lg">
                        <p className="text-sm text-blue">
                          <strong>{t('products.productName')}:</strong> {formData.product_name || <em className="text-secondary">({t('common.none')})</em>}
                        </p>
                        <p className="text-sm text-blue mt-sm">
                          <strong>{t('products.descriptionLabel')}:</strong> {formData.description || <em className="text-secondary">({t('common.none')})</em>}
                        </p>
                        <p className="text-xs text-secondary mt-md">
                          {t('products.mainLanguageEditAbove')}
                        </p>
                      </div>
                    ) : (
                      /* Show translation fields for other languages */
                      <>
                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('products.productName')} ({activeLanguageTab.toUpperCase()})
                          </label>
                          <input
                            type="text"
                            value={translations[activeLanguageTab]?.product_name || ''}
                            onChange={(e) => setTranslations(prev => ({
                              ...prev,
                              [activeLanguageTab]: {
                                product_name: e.target.value,
                                description: prev[activeLanguageTab]?.description || '',
                                variant_label: prev[activeLanguageTab]?.variant_label || '',
                                variant_description_label: prev[activeLanguageTab]?.variant_description_label || '',
                                product_specific_info: prev[activeLanguageTab]?.product_specific_info || '',
                                features: prev[activeLanguageTab]?.features || ''
                              }
                            }))}
                            className="input w-full"
                            placeholder={`Add the ${availableLanguages.find(l => l.code === activeLanguageTab)?.label} version`}
                          />
                          <p className="text-xs text-secondary mt-xs">
                            {t('products.translationHelper')}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('products.descriptionLabel')} ({activeLanguageTab.toUpperCase()})
                          </label>
                          <textarea
                            value={translations[activeLanguageTab]?.description || ''}
                            onChange={(e) => setTranslations(prev => ({
                              ...prev,
                              [activeLanguageTab]: {
                                product_name: prev[activeLanguageTab]?.product_name || '',
                                description: e.target.value,
                                variant_label: prev[activeLanguageTab]?.variant_label || '',
                                variant_description_label: prev[activeLanguageTab]?.variant_description_label || '',
                                product_specific_info: prev[activeLanguageTab]?.product_specific_info || '',
                                features: prev[activeLanguageTab]?.features || ''
                              }
                            }))}
                            className="input w-full"
                            rows={4}
                            placeholder={`Add the ${availableLanguages.find(l => l.code === activeLanguageTab)?.label} version`}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('products.productSpecificInfo')} ({activeLanguageTab.toUpperCase()})
                          </label>
                          <textarea
                            value={translations[activeLanguageTab]?.product_specific_info || ''}
                            onChange={(e) => setTranslations(prev => ({
                              ...prev,
                              [activeLanguageTab]: {
                                product_name: prev[activeLanguageTab]?.product_name || '',
                                description: prev[activeLanguageTab]?.description || '',
                                variant_label: prev[activeLanguageTab]?.variant_label || '',
                                variant_description_label: prev[activeLanguageTab]?.variant_description_label || '',
                                product_specific_info: e.target.value,
                                features: prev[activeLanguageTab]?.features || ''
                              }
                            }))}
                            className="input w-full"
                            rows={3}
                            placeholder={`Add the ${availableLanguages.find(l => l.code === activeLanguageTab)?.label} version`}
                          />
                        </div>

                        <div>
                          <JsonbTableEditor
                            value={translations[activeLanguageTab]?.features || ''}
                            onChange={(value) => setTranslations(prev => ({
                              ...prev,
                              [activeLanguageTab]: {
                                product_name: prev[activeLanguageTab]?.product_name || '',
                                description: prev[activeLanguageTab]?.description || '',
                                variant_label: prev[activeLanguageTab]?.variant_label || '',
                                variant_description_label: prev[activeLanguageTab]?.variant_description_label || '',
                                product_specific_info: prev[activeLanguageTab]?.product_specific_info || '',
                                features: value
                              }
                            }))}
                            label={`${t('products.featuresLabel')} (${activeLanguageTab.toUpperCase()})`}
                            placeholder='["750Wh battery", "Bosch CX motor", "All-terrain tires"]'
                            helperText={t('products.featuresHelperText')}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Details & Variants */}
          {activeTab === 'details' && (
            <div className="space-y-lg">
              {/* Sizes Section */}
              <div>
                <JsonbTableEditor
                  value={formData.sizes}
                  onChange={(value) => setFormData(prev => ({ ...prev, sizes: value }))}
                  label={t('products.sizesLabel')}
                  placeholder='["S", "M", "L", "XL"]'
                  helperText={t('products.sizesHelperText')}
                  columnConfigs={{
                    size: { type: 'string' }
                  }}
                />
              </div>

              {/* Bundle Components Section */}
              <div className="border-t border-primary pt-lg">
                <JsonbTableEditor
                  value={formData.bundle_components}
                  onChange={(value) => setFormData(prev => ({ ...prev, bundle_components: value }))}
                  label={t('products.bundleComponentsLabel')}
                  placeholder='[{"size": null, "quantity": 1, "product_type_id": ""}]'
                  helperText={t('products.bundleComponentsHelperText')}
                  columnConfigs={{
                    size: { type: 'string' },
                    product_type_id: {
                      displayLabel: t('products.productLabel'),
                      options: products.map(p => ({ value: p.id, label: p.product_name }))
                    }
                  }}
                />
              </div>

              {/* Variants Section */}
              <div className="border-t border-primary pt-lg">
                <h3 className="text-lg font-semibold text-primary mb-lg">{t('products.productVariantsLabel')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
                  <div>
                    <label htmlFor="variant_type" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.variantTypeLabel')}
                    </label>
                    <select
                      id="variant_type"
                      name="variant_type"
                      value={formData.variant_type}
                      onChange={handleInputChange}
                      className={`input w-full ${errors.variant_type ? 'border-danger' : ''}`}
                    >
                      <option value="">{t('products.variantTypeNotSet')}</option>
                      {VARIANT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {t(VARIANT_TYPE_LABEL_KEYS[type])}
                        </option>
                      ))}
                      {/* Keep an unsupported stored value visible so it can be replaced */}
                      {formData.variant_type && !isVariantType(formData.variant_type) && (
                        <option value={formData.variant_type}>{formData.variant_type}</option>
                      )}
                    </select>
                    {errors.variant_type && (
                      <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                        <AlertCircle className="w-3 h-3" />
                        {errors.variant_type}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="variant_label" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.variantLabel')}
                    </label>
                    <input
                      type="text"
                      id="variant_label"
                      name="variant_label"
                      value={formData.variant_label}
                      onChange={handleInputChange}
                      placeholder={t('products.variantLabelPlaceholder')}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="variant_description_label" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.variantDescriptionLabel')}
                    </label>
                    <input
                      type="text"
                      id="variant_description_label"
                      name="variant_description_label"
                      value={formData.variant_description_label}
                      onChange={handleInputChange}
                      placeholder={t('products.variantDescriptionPlaceholder')}
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Variant Translations Section */}
              {availableLanguages.length > 1 && (
                <div className="border-t border-primary pt-lg">
                  <div className="flex items-center gap-md mb-lg">
                    <Languages className="w-5 h-5 text-blue" />
                    <h3 className="text-lg font-semibold text-primary">{t('products.variantTranslations')}</h3>
                  </div>

                  {/* Language Tabs */}
                  <div className="flex gap-sm mb-lg border-b border-primary/10 overflow-x-auto">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setActiveLanguageTab(lang.code)}
                        className={`px-md py-sm text-sm font-medium whitespace-nowrap transition-colors ${
                          activeLanguageTab === lang.code
                            ? 'text-blue border-b-2 border-blue'
                            : 'text-secondary hover:text-primary'
                        }`}
                      >
                        {lang.flag} {lang.label}
                      </button>
                    ))}
                  </div>

                  {/* Translation Fields */}
                  <div className="space-y-lg">
                    {activeLanguageTab === defaultLanguage ? (
                      /* Show main table data for default language */
                      <div className="p-md bg-blue/5 border border-blue/20 rounded-lg">
                        <p className="text-sm text-blue">
                          <strong>{t('products.variantLabel')}:</strong> {formData.variant_label || <em className="text-secondary">({t('common.none')})</em>}
                        </p>
                        <p className="text-sm text-blue mt-sm">
                          <strong>{t('products.variantDescriptionLabel')}:</strong> {formData.variant_description_label || <em className="text-secondary">({t('common.none')})</em>}
                        </p>
                        <p className="text-xs text-secondary mt-md">
                          {t('products.mainLanguageEditFieldsAbove')}
                        </p>
                      </div>
                    ) : (
                      /* Show translation fields for other languages */
                      <>
                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('products.variantLabel')} ({activeLanguageTab.toUpperCase()})
                          </label>
                          <input
                            type="text"
                            value={translations[activeLanguageTab]?.variant_label || ''}
                            onChange={(e) => setTranslations(prev => ({
                              ...prev,
                              [activeLanguageTab]: {
                                product_name: prev[activeLanguageTab]?.product_name || '',
                                description: prev[activeLanguageTab]?.description || '',
                                variant_label: e.target.value,
                                variant_description_label: prev[activeLanguageTab]?.variant_description_label || '',
                                product_specific_info: prev[activeLanguageTab]?.product_specific_info || '',
                                features: prev[activeLanguageTab]?.features || ''
                              }
                            }))}
                            className="input w-full"
                            placeholder={`Add the ${availableLanguages.find(l => l.code === activeLanguageTab)?.label} version`}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary mb-sm">
                            {t('products.variantDescriptionLabel')} ({activeLanguageTab.toUpperCase()})
                          </label>
                          <input
                            type="text"
                            value={translations[activeLanguageTab]?.variant_description_label || ''}
                            onChange={(e) => setTranslations(prev => ({
                              ...prev,
                              [activeLanguageTab]: {
                                product_name: prev[activeLanguageTab]?.product_name || '',
                                description: prev[activeLanguageTab]?.description || '',
                                variant_label: prev[activeLanguageTab]?.variant_label || '',
                                variant_description_label: e.target.value,
                                product_specific_info: prev[activeLanguageTab]?.product_specific_info || '',
                                features: prev[activeLanguageTab]?.features || ''
                              }
                            }))}
                            className="input w-full"
                            placeholder={`Add the ${availableLanguages.find(l => l.code === activeLanguageTab)?.label} version`}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 3: Inventory & Booking */}
          {activeTab === 'inventory' && (
            <div className="space-y-lg">
              {/* Inventory Configuration */}
              <div>
                <h3 className="text-lg font-semibold text-primary mb-lg">{t('products.inventoryConfigurationLabel')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-lg">
                  <div>
                    <label htmlFor="inventory_type" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.inventoryTypeLabel')}
                    </label>
                    <select
                      id="inventory_type"
                      name="inventory_type"
                      value={formData.inventory_type}
                      onChange={handleInputChange}
                      className="input w-full"
                    >
                      <option value="individual">{t('products.individualItems')}</option>
                      <option value="pool">{t('products.poolQuantity')}</option>
                      <option value="unlimited">{t('products.unlimited')}</option>
                    </select>
                    <p className="text-xs text-secondary mt-xs">
                      {t('products.inventoryTypeHelperText')}
                    </p>
                  </div>

                  <div>
                    <label htmlFor="pool_quantity" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.poolQuantityLabel')}
                    </label>
                    <input
                      type="number"
                      id="pool_quantity"
                      name="pool_quantity"
                      value={formData.pool_quantity || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, pool_quantity: e.target.value ? parseInt(e.target.value) : null }))}
                      min="0"
                      placeholder={t('products.poolQuantityPlaceholder')}
                      className="input w-full"
                    />
                  </div>

                  {scanningEnabled && formData.inventory_type !== 'individual' && (
                    <div>
                      <label htmlFor="barcode" className="block text-sm font-medium text-primary mb-sm">
                        {t('scan.barcodeLabel')}
                      </label>
                      <input
                        type="text"
                        id="barcode"
                        value={formData.barcode}
                        onChange={(e) => setFormData(prev => ({ ...prev, barcode: e.target.value }))}
                        className="input w-full font-mono"
                        placeholder={t('scan.barcodePlaceholder')}
                        data-barcode-input
                        data-testid="product-barcode-input"
                      />
                      <p className="text-xs text-secondary mt-xs">{t('scan.barcodeProductHint')}</p>
                    </div>
                  )}

                  <div>
                    <label htmlFor="max_concurrent_rentals" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.maxConcurrentRentalsLabel')}
                    </label>
                    <input
                      type="number"
                      id="max_concurrent_rentals"
                      name="max_concurrent_rentals"
                      value={formData.max_concurrent_rentals || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_concurrent_rentals: e.target.value ? parseInt(e.target.value) : null }))}
                      min="1"
                      placeholder={t('products.maxConcurrentRentalsPlaceholder')}
                      className="input w-full"
                      disabled={formData.inventory_type === 'unlimited'}
                    />
                    <p className="text-xs text-secondary mt-xs">
                      {t('products.maxConcurrentRentalsHelperText')}
                    </p>
                  </div>

                  {formData.product_type !== 'addon' && (
                    <div>
                      <label htmlFor="hide_stock_count" className="block text-sm font-medium text-primary mb-sm">
                        {t('products.hideStockCountLabel')}
                      </label>
                      <select
                        id="hide_stock_count"
                        name="hide_stock_count"
                        value={formData.hide_stock_count ? 'true' : 'false'}
                        onChange={(e) => setFormData(prev => ({ ...prev, hide_stock_count: e.target.value === 'true' }))}
                        className="input w-full"
                        data-testid="product-hide-stock-count"
                      >
                        <option value="false">{t('common.no')}</option>
                        <option value="true">{t('common.yes')}</option>
                      </select>
                      <p className="text-xs text-secondary mt-xs">
                        {t('products.hideStockCountHelperText')}
                      </p>
                    </div>
                  )}

                  <div>
                    <label htmlFor="delivery_eligible" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.deliveryEligibleLabel')}
                    </label>
                    <select
                      id="delivery_eligible"
                      name="delivery_eligible"
                      value={formData.delivery_eligible ? 'true' : 'false'}
                      onChange={(e) => setFormData(prev => ({ ...prev, delivery_eligible: e.target.value === 'true' }))}
                      className="input w-full"
                    >
                      <option value="false">{t('common.no')}</option>
                      <option value="true">{t('common.yes')}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Booking Configuration */}
              <div className="border-t border-primary pt-lg">
                <h3 className="text-lg font-semibold text-primary mb-lg">{t('products.bookingConfigurationLabel')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-lg mb-lg">
                  <div>
                    <label htmlFor="pre_booking_hours" className="block text-sm font-medium text-primary mb-sm">
                      {t('products.preBookingHoursLabel')}
                    </label>
                    <NumberInput
                      value={formData.pre_booking_hours}
                      onChange={(v) => setFormData(prev => ({ ...prev, pre_booking_hours: v || 0 }))}
                      mode="int"
                      min={0}
                      className="input w-full"
                    />
                    <p className="text-xs text-secondary mt-xs">{t('products.preBookingHoursHelperText')}</p>
                  </div>
                </div>

                <div className="border border-primary rounded-lg p-md mb-lg">
                  <h4 className="text-sm font-semibold text-primary mb-md">{t('products.cutoffGroupLabel')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                    <div>
                      <label htmlFor="cutoff_days_prior" className="block text-sm font-medium text-primary mb-sm">
                        {t('products.cutoffDaysPriorLabel')}
                      </label>
                      <NumberInput
                        nullable
                        value={formData.cutoff_days_prior}
                        onChange={(v) => setFormData(prev => ({ ...prev, cutoff_days_prior: v }))}
                        mode="int"
                        min={0}
                        className="input w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="cutoff_time" className="block text-sm font-medium text-primary mb-sm">
                        {t('products.cutoffTimeLabel')}
                      </label>
                      <input
                        type="time"
                        id="cutoff_time"
                        name="cutoff_time"
                        value={formData.cutoff_time}
                        onChange={handleInputChange}
                        className="input w-full"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-secondary mt-md">
                    {t('products.cutoffHelperText')}
                  </p>
                </div>

                <div>
                  <label htmlFor="maintenance_time_hours" className="block text-sm font-medium text-primary mb-sm">
                    {t('products.maintenanceTimeHoursLabel')}
                  </label>
                  <NumberInput
                    value={formData.maintenance_time_hours}
                    onChange={(v) => setFormData(prev => ({ ...prev, maintenance_time_hours: v || 0 }))}
                    mode="float"
                    min={0}
                    step="0.25"
                    className="input w-full"
                  />
                  <p className="text-xs text-secondary mt-xs">
                    {t('products.maintenanceTimeHelperText')}
                  </p>
                </div>

                {/* Maintenance Check Intervals */}
                <div className="border-t border-primary pt-lg">
                  <div className="flex items-center gap-sm mb-md">
                    <Wrench className="w-4 h-4 text-secondary" />
                    <h4 className="text-sm font-semibold text-primary">{t('products.maintenanceChecksTitle')}</h4>
                  </div>
                  <p className="text-xs text-secondary mb-lg">
                    {t('products.maintenanceChecksDescription')}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                    <div>
                      <label className="block text-sm font-medium text-primary mb-sm">
                        {t('products.maintenanceCheckIntervalHoursLabel')}
                      </label>
                      <NumberInput
                        value={formData.maintenance_check_interval_hours ?? 0}
                        onChange={(v) => setFormData(prev => ({ ...prev, maintenance_check_interval_hours: v || null }))}
                        mode="decimal"
                        min={0}
                        className="input w-full"
                      />
                      <p className="text-xs text-secondary mt-xs">
                        {t('products.maintenanceCheckIntervalHoursHelperText')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary mb-sm">
                        {t('products.maintenanceCheckIntervalUsesLabel')}
                      </label>
                      <NumberInput
                        value={formData.maintenance_check_interval_uses ?? 0}
                        onChange={(v) => setFormData(prev => ({ ...prev, maintenance_check_interval_uses: v || null }))}
                        mode="int"
                        min={0}
                        className="input w-full"
                      />
                      <p className="text-xs text-secondary mt-xs">
                        {t('products.maintenanceCheckIntervalUsesHelperText')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 4: Display Settings */}
          {activeTab === 'display' && (
            <div className="space-y-lg">
              <h3 className="text-lg font-semibold text-primary mb-lg">{t('products.displaySettingsLabel')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-lg">
                <div>
                  <label htmlFor="active" className="block text-sm font-medium text-primary mb-sm">
                    {t('products.activeLabel')}
                  </label>
                  <select
                    id="active"
                    name="active"
                    value={formData.active ? 'true' : 'false'}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === 'true' }))}
                    className="input w-full"
                  >
                    <option value="true">{t('common.yes')}</option>
                    <option value="false">{t('common.no')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="featured" className="block text-sm font-medium text-primary mb-sm">
                    {t('products.featuredLabel')}
                  </label>
                  <select
                    id="featured"
                    name="featured"
                    value={formData.featured ? 'true' : 'false'}
                    onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.value === 'true' }))}
                    className="input w-full"
                  >
                    <option value="false">{t('common.no')}</option>
                    <option value="true">{t('common.yes')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="hidden" className="block text-sm font-medium text-primary mb-sm">
                    {t('products.hiddenLabel')}
                  </label>
                  <select
                    id="hidden"
                    name="hidden"
                    value={formData.hidden ? 'true' : 'false'}
                    onChange={(e) => setFormData(prev => ({ ...prev, hidden: e.target.value === 'true' }))}
                    className="input w-full"
                  >
                    <option value="false">{t('common.no')}</option>
                    <option value="true">{t('common.yes')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="display_order" className="block text-sm font-medium text-primary mb-sm">
                    {t('products.displayOrderLabel')}
                  </label>
                  <NumberInput
                    value={formData.display_order}
                    onChange={(v) => setFormData(prev => ({ ...prev, display_order: v || 0 }))}
                    mode="int"
                    min={0}
                    className="input w-full"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab 5: Pricing Rules (Edit mode only) */}
          {activeTab === 'pricing' && isEditMode && id && (
            <PricingRulesEditor
              ref={pricingRulesRef}
              productTypeId={id}
              currency={currency}
              locations={locations}
              productSizes={formData.sizes ? (() => {
                try {
                  const parsed = JSON.parse(formData.sizes);
                  return Array.isArray(parsed) ? parsed : undefined;
                } catch {
                  return undefined;
                }
              })() : undefined}
              onChange={setPricingRuleChanges}
              availableLanguages={availableLanguages}
              defaultLanguage={defaultLanguage}
            />
          )}

          {/* Tab 6: Linked Addons (Edit mode only, not for addon products) */}
          {activeTab === 'addons' && isEditMode && id && merchantId && formData.product_type !== 'addon' && (
            <ProductAddonsManager
              productTypeId={id}
              merchantId={merchantId}
              locationId={formData.location_id}
              currency={currency}
              onChange={setAddonChanges}
            />
          )}

          {/* Tab 7: Usage Statistics (Edit mode only) */}
          {activeTab === 'usage' && isEditMode && (
            <div className="space-y-lg">
              <div className="flex items-center gap-md mb-lg">
                <BarChart3 className="w-6 h-6 text-blue" />
                <div>
                  <h3 className="text-lg font-semibold text-primary">{t('products.usageStatistics')}</h3>
                  <p className="text-sm text-secondary">{t('products.usageStatisticsDescription')}</p>
                </div>
              </div>

              {/* Usage Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
                <div className="card">
                  <div className="flex items-start justify-between mb-md">
                    <div className="w-10 h-10 rounded-md bg-blue/10 flex items-center justify-center">
                      <RotateCcw className="w-5 h-5 text-blue" />
                    </div>
                    <span className="badge badge-info">Total</span>
                  </div>
                  <p className="text-secondary text-sm mb-xs">{t('products.totalRentals')}</p>
                  <p className="text-2xl font-bold text-primary">{formData.usage_times || 0}</p>
                </div>

                <div className="card">
                  <div className="flex items-start justify-between mb-md">
                    <div className="w-10 h-10 rounded-md bg-success/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-success" />
                    </div>
                    <span className="badge badge-success">Hours</span>
                  </div>
                  <p className="text-secondary text-sm mb-xs">{t('products.usageHours')}</p>
                  <p className="text-2xl font-bold text-primary">{formData.usage_hours || 0}h</p>
                </div>

                <div className="card">
                  <div className="flex items-start justify-between mb-md">
                    <div className="w-10 h-10 rounded-md bg-warning/10 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-warning" />
                    </div>
                    <span className="badge badge-warning">Revenue</span>
                  </div>
                  <p className="text-secondary text-sm mb-xs">{t('products.totalRevenue')}</p>
                  <p className="text-2xl font-bold text-primary">
                    {new Intl.NumberFormat(undefined, {
                      style: 'currency',
                      currency: currency || 'EUR'
                    }).format(formData.usage_revenue || 0)}
                  </p>
                </div>
              </div>

              {/* Info Note */}
              <div className="bg-blue/5 border border-blue/20 rounded-lg p-md">
                <div className="flex items-start gap-sm">
                  <Info className="w-5 h-5 text-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-primary font-medium">{t('products.usageStatsNote')}</p>
                    <p className="text-sm text-secondary mt-xs">
                      {t('products.usageStatsNoteDescription')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons - Mobile Bottom */}
      <div className="lg:hidden flex flex-col gap-md">
        {isEditMode && (
          <div className="flex items-center gap-md">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn btn-ghost text-danger hover:bg-danger/10 flex-1"
              disabled={loading || deleting}
            >
              <Trash2 className="w-4 h-4" />
              Delete Product
            </button>
            <button
              onClick={() => setShowCopyDialog(true)}
              className="btn btn-ghost flex-1"
              disabled={loading || deleting}
            >
              <Copy className="w-4 h-4" />
              {t('products.copyProduct')}
            </button>
          </div>
        )}
        <div className="flex items-center gap-md">
          <button onClick={handleCancel} className="btn btn-ghost flex-1" disabled={loading || deleting}>
            <X className="w-4 h-4" />
            {t('common.cancel')}
          </button>
          <button onClick={handleSave} className="btn btn-primary flex-1" disabled={loading || deleting}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('common.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? t('products.updateProduct') : t('common.save')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-md">
          <div className="bg-primary rounded-lg shadow-2xl max-w-md w-full p-xl">
            <div className="flex items-center gap-md mb-lg">
              <div className="w-12 h-12 rounded-full bg-danger/10 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-danger" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary">Delete Product</h3>
                <p className="text-sm text-secondary">This action cannot be undone</p>
              </div>
            </div>

            {errors.general ? (
              <>
                <div className="bg-danger/10 border border-danger/20 rounded-lg p-md mb-lg">
                  <p className="text-sm text-danger">{errors.general}</p>
                </div>
                <p className="text-secondary mb-xl">
                  Would you like to deactivate this product instead? This will mark it as inactive but preserve all data and inventory.
                </p>
                <div className="flex items-center justify-end gap-md">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setErrors({});
                    }}
                    disabled={deleting}
                    className="btn btn-ghost"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDeactivate}
                    disabled={deleting}
                    className="btn btn-secondary"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Deactivating...
                      </>
                    ) : (
                      <>
                        Deactivate Product
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-secondary mb-xl">
                  Are you sure you want to delete this product? All associated data including pricing rules and add-ons will be permanently removed.
                </p>
                <div className="flex items-center justify-end gap-md">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                    className="btn btn-ghost"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="btn bg-danger text-white hover:bg-danger/90"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Delete Product
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Copy Product Dialog */}
      {isEditMode && (
        <CopyProductDialog
          isOpen={showCopyDialog}
          onClose={() => setShowCopyDialog(false)}
          onCopy={handleCopy}
          productName={formData.product_name}
          currentLocationId={formData.location_id}
          locations={locations}
          isCopying={copying}
        />
      )}
    </div>
  );
}
